import { Column, Index } from 'typeorm';
import type { ApolloTagStatus } from '../../constants/apollo';
import { Entity } from '../../decorators';
import { BaseEntity } from './base.entity';

/** Why a person was queued for the Synced label. */
export const APOLLO_TAG_SOURCES = ['reveal', 'workd'] as const;
export type ApolloTagSource = (typeof APOLLO_TAG_SOURCES)[number];

/**
 * PD-17611 — the durable tagging log (Mission Control's
 * mc_apollo_synced_log). Every person Workd reveals, and every person it
 * already holds with an email, is labeled Synced in the tenant's Apollo
 * account so Apollo leaves them out of later people searches. Apollo caps
 * tagging per day, so people are queued here, labeled in batches of 100, and
 * whatever a day's cap leaves is finished by the nightly job; a person is
 * labeled once.
 *
 * Keyed by email because Apollo's contact list keys on it (contacts are
 * created with bulk_create and run_dedupe, then labeled with bulk_update).
 * Stored lowercase.
 *
 * Not audited (`auditable: false`): machine bookkeeping rewritten on every
 * attempt.
 *
 * SM1 exposure: none. SM1 names no such table.
 */
@Entity('apollo_tag', {}, { auditable: false })
@Index('UK_apollo_tag_email', ['email'], { unique: true })
@Index('IDX_apollo_tag_status', ['status'])
export class ApolloTagEntity extends BaseEntity {
	@Column('varchar', { name: 'email', length: 320 })
	public email!: string;

	@Column('varchar', { name: 'apolloPersonId', length: 64, nullable: true })
	public apolloPersonId!: string | null;

	@Column('varchar', { name: 'firstName', length: 100, nullable: true })
	public firstName!: string | null;

	@Column('varchar', { name: 'lastName', length: 100, nullable: true })
	public lastName!: string | null;

	@Column('int', { name: 'fk_contact__id', unsigned: true, nullable: true })
	public fkContactId!: number | null;

	@Column('varchar', { name: 'source', length: 16 })
	public source!: ApolloTagSource;

	@Column('varchar', { name: 'status', length: 16 })
	public status!: ApolloTagStatus;

	@Column('int', { name: 'attempts', unsigned: true, default: 0 })
	public attempts!: number;

	@Column('datetime', { name: 'queuedAt' })
	public queuedAt!: Date;

	@Column('datetime', { name: 'taggedAt', nullable: true })
	public taggedAt!: Date | null;

	/** The contact id in the tenant's Apollo account, once created there. */
	@Column('varchar', { name: 'apolloContactId', length: 64, nullable: true })
	public apolloContactId!: string | null;

	@Column('varchar', { name: 'lastError', length: 500, nullable: true })
	public lastError!: string | null;
}
